import React, { useState, useEffect, useReducer } from 'react'
import {
  Button,
  IconButton,
  makeStyles,
  Switch,
  FormControlLabel
} from '@material-ui/core'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import Title from '../../components/Title'
import { Add, Edit, Delete } from '@material-ui/icons'
import ClosingReasonModal from '../../components/ClosingReasonModal/ClosingReasonModal'
import ConfirmationModal from '../../components/ConfirmationModal'
import { toast } from 'react-toastify'
import { i18n } from '../../translate/i18n'
import api from '../../services/api'
import toastError from '../../errors/toastError'
import QuickTable from '../../components/QuickTable'

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: 'scroll',
    ...theme.scrollbarStyles
  }
}))

const reducer = (state, action) => {
  if (action.type === 'LOAD_CLOSING_REASONS') {
    const closingReasons = action.payload
    const newClosingReasons = []

    closingReasons.forEach(closingReason => {
      const closingReasonIndex = state.findIndex(q => q.id === closingReason.id)
      if (closingReasonIndex !== -1) {
        state[closingReasonIndex] = closingReason
      } else {
        newClosingReasons.push(closingReason)
      }
    })

    return [...state, ...newClosingReasons]
  }

  if (action.type === 'UPDATE_CLOSING_REASON') {
    const closingReason = action.payload
    const closingReasonIndex = state.findIndex(u => u.id === closingReason.id)

    if (closingReasonIndex !== -1) {
      state[closingReasonIndex] = closingReason
      return [...state]
    } else {
      return [closingReason, ...state]
    }
  }

  if (action.type === 'DELETE_CLOSING_REASON') {
    const closingReasonId = action.payload

    const closingReasonIndex = state.findIndex(q => q.id === closingReasonId)
    if (closingReasonIndex !== -1) {
      state.splice(closingReasonIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const ClosingReasons = () => {
  const classes = useStyles()

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedClosingReason, setSelectedClosingReason] = useState(null)
  const [deletingClosingReason, setDeletingClosingReason] = useState(null)
  const [closingReasonModalOpen, setClosingReasonModalOpen] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [closingReasons, dispatch] = useReducer(reducer, [])

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      fetchClosingReasons()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber])

  const fetchClosingReasons = async () => {
    try {
      const { data } = await api.get('/closing-reasons', {
        params: { searchParam, pageNumber }
      })

      dispatch({ type: 'LOAD_CLOSING_REASONS', payload: data.closingReasons })
      setHasMore(data.hasMore)
      setLoading(false)
    } catch (err) {
      toastError(err)
    }
  }

  const handleOpenClosingReasonModal = () => {
    setSelectedClosingReason(null)
    setClosingReasonModalOpen(true)
  }

  const handleCloseClosingReasonModal = (success) => {
    if (success) {
      dispatch({ type: 'RESET' });
      if (pageNumber === 1) {
        setLoading(true);
        fetchClosingReasons();
      } else {
        setPageNumber(1);
      }
    }
    setSelectedClosingReason(null);
    setClosingReasonModalOpen(false);
  }

  const handleEditClosingReason = closingReason => {
    setSelectedClosingReason(closingReason)
    setClosingReasonModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false)
    setDeletingClosingReason(null)
  }

  const handleDeleteClosingReason = async closingReasonId => {
    try {
      await api.delete(`/closing-reasons/${closingReasonId}`)
      toast.success(i18n.t('closingReasons.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingClosingReason(null)
    dispatch({ type: 'DELETE_CLOSING_REASON', payload: closingReasonId })
    setSearchParam('')
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const columns = [
    {
      field: 'id',
      headerName: i18n.t('closingReasons.table.id'),
      align: 'center'
    },
    {
      field: 'name',
      headerName: i18n.t('closingReasons.table.name')
    },
    {
      field: 'description',
      headerName: i18n.t('closingReasons.table.description')
    },
    {
      field: 'departments',
      headerName: i18n.t('closingReasons.table.departments')
    },
    {
      field: 'status',
      headerName: i18n.t('closingReasons.table.status'),
      align: 'center'
    }
  ]

  const customCellRenderer = {
    departments: row => {
      return row.queues && row.queues.length > 0
        ? row.queues.map(queue => queue.name).join(', ')
        : 'Nenhum departamento'
    },
    status: row => (
      <FormControlLabel
        control={
          <Switch
            checked={row.isActive}
            onChange={() => handleEditClosingReason(row)}
            name="isActive"
            color="primary"
          />
        }
        label={
          row.isActive
            ? i18n.t('closingReasons.table.active')
            : i18n.t('closingReasons.table.inactive')
        }
      />
    )
  }

  const customActionsRenderer = row => (
    <>
      <IconButton size="small" onClick={() => handleEditClosingReason(row)}>
        <Edit />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setConfirmModalOpen(true)
          setDeletingClosingReason(row)
        }}
      >
        <Delete />
      </IconButton>
    </>
  )

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingClosingReason &&
          `${i18n.t('closingReasons.confirmationModal.deleteTitle')} ${
            deletingClosingReason.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteClosingReason(deletingClosingReason.id)}
      >
        {i18n.t('closingReasons.confirmationModal.deleteMessage')}
      </ConfirmationModal>

      <ClosingReasonModal
        open={closingReasonModalOpen}
        onClose={handleCloseClosingReasonModal}
        aria-labelledby="form-dialog-title"
        closingReasonId={selectedClosingReason && selectedClosingReason.id}
      />

      <MainHeader>
        <Title>{i18n.t('closingReasons.title')}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenClosingReasonModal}
            startIcon={<Add />}
          >
            {i18n.t('closingReasons.buttons.add')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <QuickTable
        data={closingReasons}
        columns={columns}
        loading={loading}
        customCellRenderer={customCellRenderer}
        customActionsRenderer={customActionsRenderer}
        actions={['edit', 'delete']}
        containerProps={{
          className: classes.mainPaper,
          onScroll: handleScroll
        }}
      />
    </MainContainer>
  )
}

export default ClosingReasons

